import React from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, ArrowRight, ShieldCheck, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { scrollToSection } from '../../utils/scroll';

/* ════════════════════════════════════════════════════════════════════════════
   PRICING — Un solo plan real (Pro) + opción A medida.
   04-ago-2026: el precio del Pro se dejó de mostrar públicamente a pedido del
   usuario — todavía están en fase de captar clínicas piloto, no de producción
   general, y no quiere anclar un número que después tenga que justificar o
   cambiar. La cifra real sigue viva en SITE_CONFIG.pricing (config.ts), que es
   lo que usa ROICalculator.tsx para el cálculo del multiplicador de ROI — acá
   ya no hace falta un PRICE/SETUP_FEE locales porque no se renderiza ningún
   número. Todos los botones llevan a la demo (#cta-final): ahí es donde se
   define el precio en una conversación real.
   ════════════════════════════════════════════════════════════════════════════ */

const scrollToDemo = () => scrollToSection('#cta-final');

const CRMPricing: React.FC = () => {
  const { t } = useTranslation();

  const PRO_FEATURES = [
    t('pricing.pro_feat_1', 'Agente de IA personalizado, 24/7'),
    t('pricing.pro_feat_2', 'Bandeja de WhatsApp centralizada para tu equipo'),
    t('pricing.pro_feat_3', 'Agenda y recordatorios automáticos'),
    t('pricing.pro_feat_4', 'Pipeline de ventas visual'),
    t('pricing.pro_feat_5', 'Usuarios ilimitados'),
    t('pricing.pro_feat_6', 'Reportes y métricas en tiempo real'),
    t('pricing.pro_feat_7', 'Soporte prioritario en español'),
  ];

  const CUSTOM_FEATURES = [
    t('pricing.custom_feat_1', 'IA entrenada a tu negocio'),
    t('pricing.custom_feat_2', 'Integraciones a medida con tu software actual'),
    t('pricing.custom_feat_3', 'Onboarding asistido por nuestro equipo'),
    t('pricing.custom_feat_4', 'Account manager dedicado'),
    t('pricing.custom_feat_5', 'SLA garantizado'),
  ];

  return (
    <section id="pricing" style={{ padding: 'clamp(4.5rem, 9vh, 7.5rem) 0', position: 'relative' }}>
      <style>{`
        .price-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
          gap: 1.5rem;
          align-items: stretch;
          max-width: 920px;
          margin: 0 auto;
        }
        @media (max-width: 860px) {
          .price-grid { grid-template-columns: 1fr; max-width: 480px; }
        }
        .price-card {
          position: relative;
          border-radius: 24px;
          padding: clamp(2rem, 4vw, 2.75rem);
          display: flex;
          flex-direction: column;
          transition: transform 0.4s var(--ease-out-expo), box-shadow 0.4s var(--ease-out-expo);
        }
        .price-card--pro {
          background:
            radial-gradient(120% 80% at 50% 0%, rgba(0,229,209,0.10) 0%, transparent 60%),
            rgba(8,10,12,0.92);
          border: 1px solid rgba(0,229,209,0.35);
          box-shadow: 0 24px 60px -24px rgba(0,229,209,0.35);
        }
        .price-card--pro:hover { transform: translateY(-4px); box-shadow: 0 32px 70px -22px rgba(0,229,209,0.45); }
        .price-card--custom {
          background: rgba(255,255,255,0.022);
          border: 1px solid var(--border-light);
        }
        .price-card--custom:hover { transform: translateY(-4px); border-color: rgba(139,92,246,0.4); }
        [data-theme='light'] .price-card--pro { background: radial-gradient(120% 80% at 50% 0%, rgba(0,229,209,0.12) 0%, transparent 60%), #fff; }
        [data-theme='light'] .price-card--custom { background: rgba(0,0,0,0.015); }

        .price-cta {
          width: 100%; margin-top: auto;
          display: inline-flex; align-items: center; justify-content: center; gap: 9px;
          padding: 1rem 1.25rem; border-radius: 14px; border: none; cursor: pointer;
          font-family: var(--font-heading); font-weight: 700; font-size: 0.98rem;
          transition: transform 0.2s ease, box-shadow 0.3s ease, background 0.3s ease;
        }
        .price-cta--pro {
          color: #00201d;
          background: linear-gradient(120deg, #00e5d1 0%, #38f0c4 100%);
          box-shadow: 0 14px 34px -10px rgba(0,229,209,0.6);
        }
        .price-cta--pro:hover { transform: translateY(-2px); box-shadow: 0 20px 42px -10px rgba(0,229,209,0.7); }
        .price-cta--custom {
          color: #000;
          background: #fff;
          border: none;
          border-radius: 980px;
        }
        .price-cta--custom:hover { transform: translateY(-2px); background: #e8e8ed; }
      `}</style>

      <div className="container">
        {/* ── Encabezado ── */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.05, duration: 0.6 }}
            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: 300, color: 'var(--text-primary)', margin: '0 0 1rem', lineHeight: 1.12, letterSpacing: '-0.03em', fontFamily: 'var(--font-heading)' }}
          >
            {t('pricing.title1', 'Un plan que se paga solo con')} <span className="grad-text" style={{ fontWeight: 800 }}>{t('pricing.title2', 'una venta.')}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.12, duration: 0.6 }}
            style={{ color: 'var(--text-secondary)', maxWidth: 560, margin: '0 auto', fontSize: '1.08rem', lineHeight: 1.6 }}
          >
            {t('pricing.subtitle1', 'Sin letra chica. Recupera cada cliente que hoy se te escapa por WhatsApp — y por lanzamiento, te')} <strong style={{ color: 'var(--text-primary)' }}>{t('pricing.subtitle2', 'regalamos la instalación.')}</strong>
          </motion.p>
        </div>

        {/* ── Cards ── */}
        <div className="price-grid">
          {/* PRO — el plan principal */}
          <motion.div
            className="price-card price-card--pro"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div style={{ position: 'absolute', top: -13, left: 'clamp(2rem, 4vw, 2.75rem)', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent-teal)', color: '#00201d', padding: '5px 14px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', fontFamily: 'var(--font-heading)', boxShadow: '0 8px 22px -6px rgba(0,229,209,0.6)' }}>
              <Sparkles size={12} /> {t('pricing.badge_popular', 'MÁS POPULAR')}
            </div>

            <h3 style={{ fontSize: 'clamp(1.15rem, 3.5vw, 1.4rem)', fontWeight: 700, color: 'var(--text-primary)', margin: '0.5rem 0 0.35rem', fontFamily: 'var(--font-heading)' }}>RurayHUB Pro</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', margin: '0 0 1.5rem', lineHeight: 1.5 }}>
              {t('pricing.pro_desc', 'Todo lo que tu negocio necesita para no perder un solo mensaje.')}
            </p>

            {/* Sin precio visible (04-ago-2026, a pedido del usuario): fase de
                captar clínicas piloto, no producción general — el precio se
                define en la llamada, no se ancla un número todavía. */}
            <div style={{ marginBottom: '1.4rem' }}>
              <span style={{ fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em', fontFamily: 'var(--font-heading)' }}>
                {t('pricing.pro_price_cta', 'Conversemos sobre tu clínica')}
              </span>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.5rem 0 0' }}>
                {t('pricing.pro_price_note', 'Estamos en fase piloto: el precio se define en una llamada de 15 minutos, según tu clínica.')}
              </p>
            </div>

            {/* Ancla de promo: instalación gratis — sin el $250 tachado al
                lado (mismo criterio que el precio mensual: no anclar cifras
                todavía en fase piloto). */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, alignSelf: 'flex-start', padding: '7px 13px', borderRadius: 10, marginBottom: '1.6rem', background: 'rgba(0,229,209,0.08)', border: '1px solid rgba(0,229,209,0.22)' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--accent-teal)', fontFamily: 'var(--font-heading)' }}>{t('pricing.free_setup', 'Instalación GRATIS')}</span>
            </div>

            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.9rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              {PRO_FEATURES.map((f, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, color: 'var(--text-secondary)', fontSize: '0.94rem', lineHeight: 1.4 }}>
                  <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', background: 'rgba(0,229,209,0.14)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                    <Check size={13} color="var(--accent-teal)" strokeWidth={3} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <button className="price-cta price-cta--pro" onClick={scrollToDemo}>
              {t('pricing.btn_pro', 'Agendar demo de 15 min')} <ArrowRight size={17} />
            </button>
          </motion.div>

          {/* A MEDIDA */}
          <motion.div
            className="price-card price-card--custom"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <h3 style={{ fontSize: 'clamp(1.15rem, 3.5vw, 1.4rem)', fontWeight: 700, color: 'var(--text-primary)', margin: '0.5rem 0 0.35rem', fontFamily: 'var(--font-heading)' }}>{t('pricing.custom_title', 'A medida')}</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', margin: '0 0 1.5rem', lineHeight: 1.5 }}>
              {t('pricing.custom_desc', 'Para negocios con flujos propios que necesitan algo único.')}
            </p>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: '0.9rem' }}>
              <span style={{ fontSize: 'clamp(2.2rem, 5vw, 2.8rem)', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, letterSpacing: '-0.02em', fontFamily: 'var(--font-heading)' }}>{t('pricing.custom_price', 'Hablemos')}</span>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 1.6rem' }}>
              {t('pricing.custom_hint', 'Cotización según tus integraciones y volumen.')}
            </p>

            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.9rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              {CUSTOM_FEATURES.map((f, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, color: 'var(--text-secondary)', fontSize: '0.94rem', lineHeight: 1.4 }}>
                  <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', background: 'rgba(139,92,246,0.16)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                    <Check size={13} color="var(--accent-purple)" strokeWidth={3} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <button className="price-cta price-cta--custom" onClick={scrollToDemo}>
              {t('pricing.btn_custom', 'Hablar con ventas')} <ArrowRight size={17} />
            </button>
          </motion.div>
        </div>

        {/* ── Reaseguro ── */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '0.6rem 1.6rem', marginTop: '2.2rem', color: 'var(--text-muted)', fontSize: '0.84rem' }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><ShieldCheck size={15} color="var(--accent-teal)" /> {t('pricing.trust_1', 'Sin permanencia')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><ShieldCheck size={15} color="var(--accent-teal)" /> {t('pricing.trust_2', 'Cancelas cuando quieras')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><ShieldCheck size={15} color="var(--accent-teal)" /> {t('pricing.trust_3', 'Soporte en español')}</span>
        </motion.div>

        {/* ── Confianza / seguridad (datos reales) ── */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ delay: 0.28, duration: 0.6 }}
          style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '0.5rem 1.4rem', marginTop: '1rem', color: 'var(--text-muted)', fontSize: '0.78rem', opacity: 0.85 }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Lock size={13} color="var(--accent-purple)" /> {t('pricing.security_1', 'API oficial de Meta WhatsApp')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Lock size={13} color="var(--accent-purple)" /> {t('pricing.security_2', 'Datos cifrados')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Lock size={13} color="var(--accent-purple)" /> {t('pricing.security_3', 'Cumple LOPDP (Ecuador)')}</span>
        </motion.div>
      </div>
    </section>
  );
};

export default CRMPricing;
